import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { from, postgrestSource, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import { AggregateView, countRows } from "@vixio/forms-ui";
import {
  Button, Callout, Chart, DataTable, EmptyState, KpiRow, KpiTile, PageHeader, Panel, Section, StatusFromMap, addDaysIso, formatInt, todayIso,
} from "@vixio/ui";
import { DIMENSIONS, STAGES, STAGE_STATUS, dimensionLabel, useImgForm, type ImgResult } from "./api";

export function useImgKpis(orgId: string) {
  const since = addDaysIso(todayIso(), -30);
  return useQuery({
    queryKey: ["img", "kpis", orgId, since],
    queryFn: async () => {
      const base = () => from("img", "results").select("id", { count: "exact", head: true }).eq("org_id", orgId);
      const [total, last30, identified, ...rest] = await Promise.all([
        countRows(base()),
        countRows(base().gte("created_at", `${since}T00:00:00-03:00`)),
        countRows(base().not("person_id", "is", null)),
        ...STAGES.map((s) => countRows(base().eq("stage", s.key))),
        ...DIMENSIONS.map((d) => countRows(base().eq("weakest_dimension", d.key))),
      ]);
      return {
        total,
        last30,
        identified,
        byStage: STAGES.map((s, i) => ({ ...s, n: rest[i] ?? 0 })),
        byWeakest: DIMENSIONS.map((d, i) => ({ ...d, n: rest[STAGES.length + i] ?? 0 })),
      };
    },
  });
}

/** Valores distintos de UTM e pilar para os filtros (amostra das 2.000 mais recentes). */
function useContextOptions(orgId: string) {
  return useQuery({
    queryKey: ["img", "context-options", orgId],
    queryFn: async () => {
      const rows = await unwrap<Array<{ context: Record<string, unknown> | null }>>(
        from("img", "results").select("context").eq("org_id", orgId).order("created_at", { ascending: false }).limit(2000),
      );
      const pick = (k: string) =>
        [...new Set(rows.map((r) => r.context?.[k]).filter((v): v is string => typeof v === "string" && v.length > 0))].sort().map((v) => ({ value: v, label: v }));
      return { utm_source: pick("utm_source"), utm_campaign: pick("utm_campaign"), pillar: pick("pillar") };
    },
  });
}

export function ScoreBar({ score }: { score: number }) {
  const stage = STAGES.find((s) => score >= s.min && score <= s.max);
  return (
    <span className="vx-row" style={{ flexWrap: "nowrap", gap: 8, justifyContent: "flex-end" }}>
      <span className="num" style={{ minWidth: 24, textAlign: "right" }}>
        {score}
      </span>
      <span className={`vx-meter${stage?.tone === "crit" ? " vx-meter--crit" : stage?.tone === "warn" ? " vx-meter--warn" : ""}`} style={{ width: 56 }} aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
      </span>
    </span>
  );
}

export function ImgResultsPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const isAdmin = useHasRole("admin");
  const form = useImgForm(org.id);
  const kpis = useImgKpis(org.id);
  const opts = useContextOptions(org.id);
  const install = useAction(() => rpc("img", "install", { p_org: org.id }), { invalidate: [["img"], ["forms"]], success: "IMG instalado: formulário publicado e devolutivas padrão criadas" });

  const source = useMemo(
    () =>
      postgrestSource<ImgResult>({
        schema: "img",
        table: "v_results",
        orgId: org.id,
        searchColumns: ["person_name", "person_email"],
        filters: {
          utm_source: (qb, v) => qb.eq("context->>utm_source", v),
          utm_campaign: (qb, v) => qb.eq("context->>utm_campaign", v),
          pillar: (qb, v) => qb.eq("context->>pillar", v),
          identified: (qb, v) => (v === "sim" ? qb.not("person_id", "is", null) : qb.is("person_id", null)),
        },
        tiebreak: "id",
      }),
    [org.id],
  );

  const k = kpis.data;
  const pctIdentified = k && k.total > 0 ? Math.round((k.identified / k.total) * 100) : 0;

  return (
    <div className="vx-content">
      <PageHeader
        title="Índice de Maturidade Gerencial"
        description="Resultados do IMG respondido no site: pontuação 0 a 100, estágio, dimensão mais frágil e devolutiva. Instrumento em rascunho, não validado."
        actions={
          <Button icon={<MessageSquareText />} onClick={() => navigate("/diagnostico/img/devolutiva")}>
            Modelos da devolutiva
          </Button>
        }
      />
      {form.data === null ? (
        <div className="vx-section">
          <Callout tone="warn" title="IMG não instalado nesta organização">
            Instalar cria e publica o formulário do IMG (25 itens, 5 dimensões) e os modelos padrão da devolutiva.
            {isAdmin ? (
              <div style={{ marginTop: 8 }}>
                <Button variant="primary" loading={install.isPending} onClick={() => install.mutate(undefined)}>
                  Instalar IMG
                </Button>
              </div>
            ) : null}
          </Callout>
        </div>
      ) : null}
      <KpiRow>
        <KpiTile label="Resultados" value={formatInt(k?.total ?? 0)} foot="desde o início" />
        <KpiTile label="Últimos 30 dias" value={formatInt(k?.last30 ?? 0)} />
        <KpiTile label="Identificados" value={`${formatInt(k?.identified ?? 0)}`} foot={`${pctIdentified}% com consentimento de devolutiva`} />
        <KpiTile
          label="Crítico ou reativo"
          value={formatInt((k?.byStage[0]?.n ?? 0) + (k?.byStage[1]?.n ?? 0))}
          foot="abaixo de 60 pontos"
          tone={(k?.byStage[0]?.n ?? 0) > 0 ? "warn" : undefined}
        />
      </KpiRow>
      {k && k.total > 0 ? (
        <div className="vx-grid vx-grid--2 vx-section">
          <Panel title="Resultados por estágio">
            <Chart type="bar" title="Resultados por estágio" labels={k.byStage.map((s) => s.label)} series={[{ name: "Resultados", values: k.byStage.map((s) => s.n) }]} format={(v) => formatInt(v)} height={200} />
          </Panel>
          <Panel title="Dimensão mais frágil">
            <Chart
              type="bar"
              title="Quantas vezes cada dimensão foi a mais frágil"
              labels={k.byWeakest.map((d) => d.label.split(" ")[0]!)}
              series={[{ name: "Resultados", values: k.byWeakest.map((d) => d.n) }]}
              format={(v) => formatInt(v)}
              height={200}
            />
          </Panel>
        </div>
      ) : null}
      <Section title="Resultados" id="resultados">
        <DataTable<ImgResult>
          caption="Resultados do IMG"
          source={source}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/diagnostico/img/${r.id}`)}
          initialSort={{ key: "created_at", dir: "desc" }}
          searchPlaceholder="Buscar por nome ou e-mail"
          exportName="img-resultados"
          empty={
            <EmptyState title="Nenhum resultado ainda">
              Os resultados chegam pelo diagnóstico do site (img.submit). Cada envio aparece aqui na hora.
            </EmptyState>
          }
          filters={[
            { key: "stage", label: "Estágio", all: "Todos os estágios", options: STAGES.map((s) => ({ value: s.key, label: s.label })) },
            { key: "weakest_dimension", label: "Dimensão frágil", all: "Todas as dimensões", options: DIMENSIONS.map((d) => ({ value: d.key, label: d.label })) },
            { key: "utm_source", label: "Origem (utm_source)", all: "Todas as origens", options: opts.data?.utm_source ?? [] },
            { key: "utm_campaign", label: "Campanha", all: "Todas as campanhas", options: opts.data?.utm_campaign ?? [] },
            { key: "pillar", label: "Pilar", all: "Todos os pilares", options: opts.data?.pillar ?? [] },
            { key: "identified", label: "Identificação", all: "Identificados e anônimos", options: [{ value: "sim", label: "Identificados" }, { value: "nao", label: "Anônimos" }] },
          ]}
          columns={[
            { key: "created_at", header: "Data", kind: "datetime", sortable: true },
            {
              key: "person_name",
              header: "Respondente",
              sortable: true,
              cell: (r) => (
                <span className="vx-table__primary">
                  <Link to={`/diagnostico/img/${r.id}`} onClick={(e) => e.stopPropagation()}>
                    {r.person_name ?? "Resposta anônima"}
                  </Link>
                  {r.person_email ? <span className="vx-table__sub">{r.person_email}</span> : null}
                </span>
              ),
              csv: (r) => r.person_name ?? "anônimo",
            },
            { key: "score", header: "Pontuação", sortable: true, align: "right", cell: (r) => <ScoreBar score={r.score} />, csv: (r) => r.score },
            { key: "stage", header: "Estágio", cell: (r) => <StatusFromMap map={STAGE_STATUS} value={r.stage} />, csv: (r) => STAGE_STATUS[r.stage]?.label ?? r.stage },
            { key: "weakest_dimension", header: "Dimensão frágil", cell: (r) => dimensionLabel(r.weakest_dimension), csv: (r) => dimensionLabel(r.weakest_dimension) },
            {
              key: "origin",
              header: "Origem",
              cell: (r) =>
                r.context?.utm_source || r.context?.utm_campaign ? (
                  <span className="vx-table__primary">
                    {r.context.utm_source ?? <span className="muted">-</span>}
                    {r.context.utm_campaign ? <span className="vx-table__sub">{r.context.utm_campaign}</span> : null}
                  </span>
                ) : (
                  <span className="muted">direto</span>
                ),
              csv: (r) => [r.context?.utm_source, r.context?.utm_campaign].filter(Boolean).join(" / "),
            },
            { key: "pillar", header: "Pilar", cell: (r) => r.context?.pillar ?? <span className="muted">-</span>, csv: (r) => r.context?.pillar ?? "" },
          ]}
        />
      </Section>
      {form.data ? (
        <Section title={`Médias por dimensão (agregado com k = ${form.data.min_group_size})`} id="agregado">
          <Panel>
            <AggregateView formId={form.data.id} scaleLabel="1 a 5" />
          </Panel>
        </Section>
      ) : null}
    </div>
  );
}
